import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  Info, 
  AlertTriangle, 
  CheckCircle, 
  AlertCircle, 
  Megaphone,
  X 
} from 'lucide-react';
import { usePublicSportsHubMessages, SportsHubMessage } from '@/hooks/useSportsHubMessages';
import { Button } from '@/components/ui/button';

interface SportsHubMessagesProps {
  sportId?: string;
  sportName?: string;
  className?: string;
}

const SportsHubMessages: React.FC<SportsHubMessagesProps> = ({ 
  sportId, 
  sportName,
  className = "" 
}) => {
  const { messages, loading } = usePublicSportsHubMessages(sportId);
  const [dismissedMessages, setDismissedMessages] = React.useState<string[]>([]);

  const getMessageIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <Info className="w-4 h-4" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      case 'success':
        return <CheckCircle className="w-4 h-4" />;
      case 'error':
        return <AlertCircle className="w-4 h-4" />;
      case 'announcement':
        return <Megaphone className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getAlertVariant = (type: string): "default" | "destructive" => {
    switch (type) {
      case 'error':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getMessageStyle = (type: string) => {
    switch (type) {
      case 'info':
        return 'border-l-blue-500 bg-blue-50 text-blue-900';
      case 'warning':
        return 'border-l-yellow-500 bg-yellow-50 text-yellow-900';
      case 'success':
        return 'border-l-green-500 bg-green-50 text-green-900';
      case 'error':
        return 'border-l-red-500 bg-red-50 text-red-900';
      case 'announcement':
        return 'border-l-purple-500 bg-purple-50 text-purple-900';
      default:
        return 'border-l-gray-500 bg-gray-50 text-gray-900';
    }
  };

  const handleDismiss = (messageId: string) => {
    setDismissedMessages(prev => [...prev, messageId]);
  };

  if (loading || messages.length === 0) {
    return null;
  }

  const visibleMessages = messages.filter(msg => !dismissedMessages.includes(msg.id));

  if (visibleMessages.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {visibleMessages.map((message) => (
        <Alert
          key={message.id}
          variant={getAlertVariant(message.message_type)}
          className={`relative border-l-4 ${getMessageStyle(message.message_type)} shadow-sm`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              {getMessageIcon(message.message_type)}
              <div className="flex-1">
                <AlertTitle className="text-sm font-semibold mb-1 flex items-center gap-2">
                  {message.title}
                  <div className="flex gap-1">
                    {message.is_global && (
                      <Badge variant="outline" className="text-xs py-0 h-5">
                        Global
                      </Badge>
                    )}
                    {!message.is_global && message.sport && (
                      <Badge variant="secondary" className="text-xs py-0 h-5">
                        {message.sport.name}
                      </Badge>
                    )}
                  </div>
                </AlertTitle>
                <AlertDescription className="text-sm leading-relaxed">
                  {message.message}
                </AlertDescription>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-white/50"
              onClick={() => handleDismiss(message.id)}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </Alert>
      ))}
    </div>
  );
};

export default SportsHubMessages;